/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ConnectionKey } from '../models/ConnectionKey';
import type { DeviceResponse } from '../models/DeviceResponse';
import type { HvpState } from '../models/HvpState';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class HvpService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Get the current HVP state of the device.
     * Get the current HVP state of the device.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hvp">Handy Vibration Protocol (HVP)</a> for additional information about the HVP protocol and code samples.
     *
     * @param xConnectionKey Device connection key.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HVP state.
     * @throws ApiError
     */
    public getHvpState(
        xConnectionKey: ConnectionKey,
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HvpState;
    })> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/hvp/state',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

    /**
     * Set the current HVP state of the device.
     * Set the HVP state of the device.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hvp">Handy Vibration Protocol (HVP)</a> for additional information about the HVP protocol and code samples.
     *
     * @param xConnectionKey Device connection key.
     * @param requestBody HVP set
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HVP state.
     * @throws ApiError
     */
    public setHvpState(
        xConnectionKey: ConnectionKey,
        requestBody: {
            /**
             * The amplitude. A value between 0 and 1 (0-100%). For LRA, ERM and Sliders with vibration adapter (percent of valid output range)
             */
            amplitude: number;
            /**
             * The frequency in Hz. 0-10000Hz. For LRA only
             */
            frequency: number;
            /**
             * The position of the vibration (mm). Where on the slider should the vibration be. For slider with vibration adapter only.
             */
            position: number;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HvpState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hvp/state',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Start the HVP playback.
     * Start the HVP playback.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hvp">Handy Vibration Protocol (HVP)</a> for additional information about the HVP protocol and code samples.
     *
     * @param xConnectionKey Device connection key.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HVP state.
     * @throws ApiError
     */
    public hvpStart(
        xConnectionKey: ConnectionKey,
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HvpState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hvp/start',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

    /**
     * Stop the HVP playback.
     * Stop the HVP playback.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hvp">Handy Vibration Protocol (HVP)</a> for additional information about the HVP protocol and code samples.
     *
     * @param xConnectionKey Device connection key.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HVP state.
     * @throws ApiError
     */
    public hvpStop(
        xConnectionKey: ConnectionKey,
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HvpState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hvp/stop',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

}
